import { CSSProperties, ReactNode, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleAlert, Database, Cog, Globe } from 'lucide-react';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';
import { varAlpha } from '../utils/statusColors';

// ─── Types ─────────────────────────────────────────────────────────────────────
interface CheckResult {
  status: 'ok' | 'error';
  latency_ms?: number;
  error?: string;
}

interface HealthData {
  status: 'healthy' | 'unhealthy';
  uptime: string;
  checks: Record<string, CheckResult>;
  version: { go: string; env: string };
}

type FetchState = 'idle' | 'loading' | 'success' | 'error';

// ─── Constants ─────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL || '';
const POLL_INTERVAL_MS = 30_000; // auto-refresh every 30s

// ─── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, CSSProperties> = {
  page:    { background: 'var(--bg)', color: 'var(--ink)', fontFamily: "'Plus Jakarta Sans', 'DM Sans', sans-serif", minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  navInner:{ maxWidth: 1100, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem' },
  navLink: { color: 'var(--ink-muted)', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none', transition: 'color 0.2s', background: 'none', border: 'none', cursor: 'pointer' },
  main:    { flex: 1, maxWidth: 900, margin: '0 auto', width: '100%', padding: '3rem 2rem 4rem' },

  // Hero banner
  banner:  { padding: '3rem 0 2rem', borderBottom: '1px solid var(--border)', marginBottom: '2.5rem' },
  bannerTop: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' },
  pageTitle: { fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 900, color: 'var(--ink)', margin: 0 },
  pageSubtitle: { color: 'var(--ink-muted)', fontSize: '1rem', margin: 0 },

  // Overall badge
  overallBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1rem', borderRadius: 9999, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.05em' },

  // Meta row (uptime / env / go / last checked)
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '1.5rem' },
  metaItem: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  metaLabel: { fontSize: '0.7rem', textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'var(--ink-muted)', fontWeight: 700 },
  metaValue: { color: 'var(--ink)', fontWeight: 700, fontSize: '1rem' },

  // Services section
  sectionTitle: { color: 'var(--ink-muted)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.1em', marginBottom: '1rem' },
  checksGrid:  { display: 'flex', flexDirection: 'column', gap: '0.75rem' },

  // Check card
  checkCard: { background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 16, padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', transition: 'border-color 0.2s' },
  checkLeft: { display: 'flex', alignItems: 'center', gap: '1rem' },
  checkIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 },
  checkName: { color: 'var(--ink)', fontWeight: 700, fontSize: '1rem', margin: '0 0 0.15rem' },
  checkDesc: { color: 'var(--ink-muted)', fontSize: '0.8rem', margin: 0 },
  checkRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem' },
  checkStatusPill: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.75rem', borderRadius: 9999, fontWeight: 700, fontSize: '0.75rem' },
  checkLatency: { color: 'var(--ink-muted)', fontSize: '0.75rem' },

  // Refresh + last updated
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', background: varAlpha('accent', 0.12), color: 'var(--accent)', border: 'none', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', transition: 'background 0.2s' },
  lastChecked: { color: 'var(--ink-muted)', fontSize: '0.8rem' },

  skeleton: { background: 'linear-gradient(90deg, var(--track) 25%, var(--skeleton-mid) 50%, var(--track) 75%)', backgroundSize: '400%', borderRadius: 12, height: 76, animation: 'shimmer 1.5s infinite' },

  // Error state
  errorBox: { background: varAlpha('danger', 0.12), border: `1px solid ${varAlpha('danger', 0.25)}`, borderRadius: 16, padding: '2rem', textAlign: 'center' as const },
  errorIcon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
  errorTitle: { color: 'var(--danger)', fontWeight: 800, fontSize: '1.1rem', margin: '0 0 0.5rem' },
  errorMsg: { color: 'var(--ink-muted)', fontSize: '0.9rem', margin: 0 },

  // Footer
  footer: { background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '2rem', textAlign: 'center' as const },
  footerCopy: { color: 'var(--ink-muted)', fontSize: '0.75rem', margin: 0 },
};

// ─── Check metadata ─────────────────────────────────────────────────────────────
const CHECK_META: Record<string, { icon: ReactNode; label: string; desc: string }> = {
  database: { icon: <Database size={20} aria-hidden="true" />, label: 'Database', desc: 'PostgreSQL connection pool health' },
};

// ─── Helper: status colours ─────────────────────────────────────────────────────
function statusColours(ok: boolean) {
  return ok
    ? { bg: varAlpha('ok', 0.12),     fg: 'var(--ok)',     dot: 'var(--ok)'     }
    : { bg: varAlpha('danger', 0.12), fg: 'var(--danger)', dot: 'var(--danger)' };
}

function overallColours(status: HealthData['status'] | null, fetchState: FetchState) {
  if (fetchState === 'loading' || fetchState === 'idle') return { bg: varAlpha('warn', 0.12),   fg: 'var(--warn)'   };
  if (fetchState === 'error')                             return { bg: varAlpha('danger', 0.12), fg: 'var(--danger)' };
  if (status === 'healthy')                              return { bg: varAlpha('ok', 0.12),     fg: 'var(--ok)'     };
  return { bg: varAlpha('danger', 0.12), fg: 'var(--danger)' };
}

function overallLabel(status: HealthData['status'] | null, fetchState: FetchState) {
  if (fetchState === 'loading') return '⟳ Checking…';
  if (fetchState === 'error')   return '● Unreachable';
  if (status === 'healthy')     return '● All Systems Operational';
  if (status === 'unhealthy')   return '● Partial Outage';
  return '● Unknown';
}

// ─── Component ──────────────────────────────────────────────────────────────────
export function HealthPage() {
  usePageTitle('System Health');
  const navigate = useNavigate();
  const [data, setData]             = useState<HealthData | null>(null);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [scrolled, setScrolled]     = useState(false);

  const fetchHealth = useCallback(async () => {
    setFetchState('loading');
    try {
      const res = await fetch(`${API_BASE}/health`);
      const json: HealthData = await res.json();
      setData(json);
      setFetchState('success');
      setLastChecked(new Date());
    } catch {
      setFetchState('error');
      setLastChecked(new Date());
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchHealth]);

  // Scroll detection for nav
  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  const oc = overallColours(data?.status ?? null, fetchState);
  const { theme } = useTheme();

  const navStyle: CSSProperties = {
    position: 'sticky', top: 0, zIndex: 50, width: '100%', height: 64,
    display: 'flex', alignItems: 'center',
    background: scrolled ? varAlpha('surface', 0.97) : varAlpha('surface', 0.80),
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    borderBottom: scrolled ? `1px solid ${varAlpha('accent', 0.2)}` : `1px solid ${varAlpha('accent', 0.08)}`,
    boxShadow: scrolled ? 'var(--shadow-lg)' : 'none',
    transition: 'background 0.3s, box-shadow 0.3s',
  };

  return (
    <div style={s.page}>
      {/* ── Nav ── */}
      <header style={navStyle}>
        <div style={s.navInner}>
          <KaziAtlasLogo size={30} onLight={theme === 'light'} />
          <nav style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
            <button style={s.navLink} onClick={() => navigate('/')}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}>
              ← Home
            </button>
            <button style={s.navLink} onClick={() => navigate('/login')}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-muted)')}>
              Log In
            </button>
            <button
              style={{ ...s.navLink, background: 'var(--accent)', color: 'var(--on-accent)', padding: '0.5rem 1rem', borderRadius: 8 }}
              onClick={() => navigate('/register')}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
              Get Started
            </button>
          </nav>
        </div>
      </header>

      <main style={s.main}>
        {/* ── Page banner ── */}
        <div style={s.banner}>
          <div style={s.bannerTop}>
            <h1 style={s.pageTitle}>System Status</h1>
            <span style={{ ...s.overallBadge, background: oc.bg, color: oc.fg }}>
              {overallLabel(data?.status ?? null, fetchState)}
            </span>
          </div>
          <p style={s.pageSubtitle}>
            Live health and uptime information for KaziAtlas services.
            Auto-refreshes every 30 seconds.
          </p>

          {/* Meta stats */}
          {data && fetchState === 'success' && (
            <div style={s.metaRow}>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Server Uptime</span>
                <span style={s.metaValue}>{data.uptime}</span>
              </div>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Environment</span>
                <span style={{ ...s.metaValue, textTransform: 'capitalize' }}>{data.version.env}</span>
              </div>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Go Runtime</span>
                <span style={s.metaValue}>{data.version.go}</span>
              </div>
              <div style={s.metaItem}>
                <span style={s.metaLabel}>Services</span>
                <span style={s.metaValue}>{Object.keys(data.checks).length} monitored</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Toolbar ── */}
        <div style={s.toolbar}>
          <p style={s.sectionTitle}>Service Health</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {lastChecked && (
              <span style={s.lastChecked}>
                Last checked: {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <button
              style={s.refreshBtn}
              onClick={fetchHealth}
              disabled={fetchState === 'loading'}
              onMouseEnter={e => (e.currentTarget.style.background = varAlpha('accent', 0.2))}
              onMouseLeave={e => (e.currentTarget.style.background = varAlpha('accent', 0.12))}>
              {fetchState === 'loading' ? '⟳ Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        </div>

        {/* ── States ── */}
        {fetchState === 'error' && (
          <div style={s.errorBox}>
            <div style={{ ...s.errorIcon, display: 'flex', justifyContent: 'center', color: 'var(--danger)' }}><CircleAlert size={40} aria-hidden="true" /></div>
            <p style={s.errorTitle}>Cannot reach the API server</p>
            <p style={s.errorMsg}>
              The server may be down or unreachable. Check your network connection and try again.
            </p>
          </div>
        )}

        {(fetchState === 'idle' || fetchState === 'loading') && !data && (
          <div style={s.checksGrid}>
            {[1, 2].map(i => (
              <div key={i} style={s.skeleton} />
            ))}
          </div>
        )}

        {/* ── Check cards ── */}
        {data && (
          <div className="hud-corner" style={s.checksGrid}>
            {Object.entries(data.checks).map(([key, check]) => {
              const meta = CHECK_META[key] ?? { icon: <Cog size={20} aria-hidden="true" />, label: key, desc: '' };
              const ok   = check.status === 'ok';
              const col  = statusColours(ok);
              return (
                <div
                  key={key}
                  style={{
                    ...s.checkCard,
                    borderColor: ok ? 'var(--border)' : varAlpha('danger', 0.3),
                    boxShadow: ok ? 'none' : `0 0 20px ${varAlpha('danger', 0.06)}`,
                  }}>
                  <div style={s.checkLeft}>
                    <div style={{ ...s.checkIcon, background: col.bg }}>
                      <span>{meta.icon}</span>
                    </div>
                    <div>
                      <p style={s.checkName}>{meta.label}</p>
                      {check.error
                        ? <p style={{ ...s.checkDesc, color: 'var(--danger)' }}>{check.error}</p>
                        : <p style={s.checkDesc}>{meta.desc}</p>}
                    </div>
                  </div>
                  <div style={s.checkRight}>
                    <span style={{ ...s.checkStatusPill, background: col.bg, color: col.fg }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: col.dot, display: 'inline-block' }} />
                      {ok ? 'Operational' : 'Degraded'}
                    </span>
                    {check.latency_ms !== undefined && (
                      <span style={s.checkLatency}>{check.latency_ms.toFixed(1)} ms</span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Always show an "API Server" card reflecting the overall fetch status */}
            <div
              style={{
                ...s.checkCard,
                borderColor: fetchState === 'success' ? 'var(--border)' : varAlpha('danger', 0.3),
              }}>
              <div style={s.checkLeft}>
                <div style={{ ...s.checkIcon, background: varAlpha('ok', 0.12), color: 'var(--ok)' }}><Globe size={20} aria-hidden="true" /></div>
                <div>
                  <p style={s.checkName}>API Server</p>
                  <p style={s.checkDesc}>HTTP server reachability and response</p>
                </div>
              </div>
              <div style={s.checkRight}>
                <span style={{ ...s.checkStatusPill, background: varAlpha('ok', 0.12), color: 'var(--ok)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)', display: 'inline-block' }} />
                  Operational
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Live heartbeat indicator ── */}
        <div style={{ marginTop: '3rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: fetchState === 'error' ? 'var(--danger)' : 'var(--ok)',
            display: 'inline-block',
            boxShadow: fetchState === 'error'
              ? `0 0 0 4px ${varAlpha('danger', 0.15)}`
              : `0 0 0 4px ${varAlpha('ok', 0.15)}`,
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          <span style={{ color: 'var(--ink-muted)', fontSize: '0.8rem' }}>
            {fetchState === 'error'
              ? 'Connection lost — retrying in 30s'
              : 'Monitoring active — refreshing every 30s'}
          </span>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer style={s.footer}>
        <p style={s.footerCopy}>© 2025 KaziAtlas · System Status</p>
      </footer>

      {/* ── Keyframe animations ── */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 100% 50%; }
          100% { background-position: 0%   50%; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
